import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
  isToday,
  parseISO,
  isWithinInterval,
} from "date-fns";

const ds = (d) => format(d, "yyyy-MM-dd");
const COLORS = {
  holiday: { bg: "#EAF0F5", text: "#3F5E7D", dot: "#5C7E9E" },
  sick: { bg: "#FBEDE7", text: "#A35C3E", dot: "#D48B71" },
};

export default function TeamCalendar() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [data, setData] = useState({ entries: [], holidays: [] });

  const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });

  const load = useCallback(() => {
    api.get("/calendar", { params: { start: ds(gridStart), end: ds(gridEnd) } }).then((r) => setData(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  useEffect(() => { load(); }, [load]);

  const days = [];
  let d = gridStart;
  while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }

  const entriesFor = (day) =>
    data.entries.filter((e) =>
      isWithinInterval(day, { start: parseISO(e.start_date), end: parseISO(e.end_date) })
    );
  const holidayFor = (day) => data.holidays.find((h) => h.date === ds(day));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Team Calendar</h1>
          <p className="text-sm mt-1" style={{ color: "#6A736D" }}>See who's off and when across the team.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="grid place-items-center w-9 h-9 rounded-md border bg-white hover:bg-[#F4F3ED]" style={{ borderColor: "#EAE8E1" }}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span data-testid="cal-month-label" className="font-heading font-medium text-lg w-40 text-center">{format(cursor, "MMMM yyyy")}</span>
          <button data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))} className="grid place-items-center w-9 h-9 rounded-md border bg-white hover:bg-[#F4F3ED]" style={{ borderColor: "#EAE8E1" }}>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.holiday.dot }} /> Holiday</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.sick.dot }} /> Sick</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#948A7B" }} /> Public holiday</span>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: "#EAE8E1" }}>
        <div className="grid grid-cols-7">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d2) => (
            <div key={d2} className="text-center text-xs uppercase tracking-wider font-medium py-2.5 border-b" style={{ color: "#6A736D", borderColor: "#EAE8E1" }}>{d2}</div>
          ))}
          {days.map((day) => {
            const entries = entriesFor(day);
            const hol = holidayFor(day);
            const inMonth = isSameMonth(day, cursor);
            return (
              <div
                key={ds(day)}
                data-testid={`cal-day-${ds(day)}`}
                className="min-h-[104px] border-b border-r p-1.5 align-top"
                style={{ borderColor: "#EAE8E1", backgroundColor: inMonth ? "#fff" : "#FAF9F6" }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-medium grid place-items-center w-6 h-6 rounded-full ${isToday(day) ? "text-white" : ""}`}
                    style={isToday(day) ? { backgroundColor: "#647D6C" } : { color: inMonth ? "#2C332E" : "#B6BBB5" }}
                  >
                    {format(day, "d")}
                  </span>
                  {hol && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#948A7B" }} title={hol.name} />}
                </div>
                <div className="mt-1 space-y-1">
                  {hol && <div className="text-[10px] truncate" style={{ color: "#948A7B" }}>{hol.name}</div>}
                  {entries.slice(0, 3).map((e) => {
                    const c = COLORS[e.type];
                    return (
                      <div
                        key={e.id}
                        className="text-[11px] leading-tight px-1.5 py-0.5 rounded truncate"
                        style={{ backgroundColor: c.bg, color: c.text, opacity: e.status === "pending" ? 0.6 : 1 }}
                        title={`${e.user_name} — ${e.type}${e.status === "pending" ? " (pending)" : ""}`}
                      >
                        {e.user_name.split(" ")[0]}{e.duration === "half" ? " ½" : ""}
                      </div>
                    );
                  })}
                  {entries.length > 3 && <div className="text-[10px]" style={{ color: "#6A736D" }}>+{entries.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
